/**
 * Aba "Questionário Avaliativo" da ficha da cliente, idêntica à do legado (`admin.html:2758-2763`,
 * `5036-5043`, `5124-5147`; linhas 209 e 210 do inventário, e a trava de botão da linha 179).
 *
 * A nota fixa e as 15 perguntas em texto de duas linhas, com os rótulos exatos (a última é o
 * depoimento); o salvar grava as 15 respostas com "Salvando...", "Salvo!" (some em 3 s) ou
 * "Erro ao salvar: " com a mensagem crua.
 *
 * Território da frente A7. A moldura importa esta aba pelo nome `AbaAvaliativo`.
 */

// Estado e efeito do React.
import { useEffect, useState } from 'react';
// As peças do Chassi.
import { Botao, CampoTexto } from '@complexity/ui';
// A leitura e a gravação do questionário (A2).
import { lerAvaliativo, salvarAvaliativo } from '@studiolu/dados/areas/ficha';
// A fonte de dados do painel.
import { usePainel } from '../../../contexto.js';
// O parágrafo do status, peça comum (o texto em si vive com a página, ver `status-da-pagina.ts`).
import { LinhaDeStatus } from '../../../comum/status-temporario.js';
// O status que vive com a página, e não com a aba (achado da verificação da A7, linha 210).
import { criarStatusDaPagina, useStatusDaPagina } from './coloracao/status-da-pagina.js';
// O fechamento de todo acordeão, a primeira linha do `carregarQuestionarios` (linha 205).
import { fecharAcordeoes } from './coloracao/acordeao.js';
// O contrato da aba.
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
// As 15 perguntas, coluna por rótulo, na ordem do legado (A2).
import { PERGUNTAS_AVALIATIVO } from '../constantes.js';
// O escopo estável, o recorte por papel e o aviso de ficha ausente, das abas da ficha (A3).
import { useEscopoEstavel } from './comum-a3/escopo.js';
import { useRecortePorPapel } from './comum-a3/papel.js';
import { AbaSemFicha } from './comum-a3/sem-ficha.js';

// As colunas na ordem do legado, que é a ordem da tela e a do registro gravado (`5127`, `5140`).
const CAMPOS = Object.keys(PERGUNTAS_AVALIATIVO);
// O `#status-avaliativo` do legado (`admin.html:2763`): um só, criado com o módulo e vivo com a
// página. Nenhuma leitura o limpa, então o texto fica ao trocar de aba e até ao abrir outra cliente.
const statusAvaliativo = criarStatusDaPagina();

/** As respostas da tela, por coluna. */
type Respostas = Readonly<Record<string, string>>;

/** A linha lida vira as respostas: `avaliativo?.[campo] || ''` para cada pergunta (`5040-5042`). */
function respostasDaLinha(linha: Readonly<Record<string, unknown>> | null): Respostas {
  // `String` porque o `textarea.value` do legado converte para texto o que receber.
  return Object.fromEntries(CAMPOS.map((campo) => [campo, String(linha?.[campo] || '')]));
}

export function AbaAvaliativo({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nada (ver `sem-ficha.tsx`).
  if (!ficha) return <AbaSemFicha aba="Questionário Avaliativo" />;
  return <ConteudoAvaliativo ficha={ficha} />;
}

function ConteudoAvaliativo({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const { somenteLeitura } = useRecortePorPapel();
  const escopo = useEscopoEstavel(ficha);
  const [respostas, setRespostas] = useState<Respostas>(() => respostasDaLinha(null));
  // O "Salvar" nasce travado e só destrava depois da leitura (linha 179, `admin.html:5971` e
  // `5050`): salvar antes gravaria as respostas que estivessem na tela, de outra cliente.
  const [carregado, setCarregado] = useState(false);
  const status = useStatusDaPagina(statusAvaliativo);

  // A leitura ao abrir a aba, a parte do avaliativo do `carregarQuestionarios` (`admin.html:5036-5043`).
  // O legado NÃO limpa este status ao ler (ao contrário das outras abas), e aqui também não: o texto
  // mora em `statusAvaliativo`, fora da aba, e sobrevive a ela desmontar.
  useEffect(() => {
    // Uma resposta que chega depois de a aba trocar de cliente não escreve na tela.
    let valendo = true;
    setCarregado(false);
    // A primeira linha do `carregarQuestionarios`, que o legado roda ao entrar nesta aba
    // (`5021`, `5069`): todo acordeão fecha, inclusive o texto explicativo da Coloração.
    fecharAcordeoes();
    void lerAvaliativo(dados, escopo).then((resultado) => {
      if (!valendo) return;
      // DEFEITO DO LEGADO REPRODUZIDO: falha na leitura só vai para o console, as respostas voltam
      // vazias e o "Salvar" destrava, então salvar gravaria o vazio por cima (`5040`, `5050`).
      if (!resultado.ok) console.error('Erro ao carregar questionário avaliativo:', resultado.erro.mensagem);
      setRespostas(respostasDaLinha(resultado.ok ? (resultado.dados as Readonly<Record<string, unknown>> | null) : null));
      setCarregado(true);
    });
    return () => {
      valendo = false;
    };
  }, [dados, escopo]);

  // "Salvar / reenviar questionário avaliativo" (`admin.html:5136-5147`). A resposta escreve no
  // parágrafo da página mesmo com a aba fora da tela, como o legado escreve no nó do DOM (`5145`).
  const salvar = async () => {
    statusAvaliativo.escrever('Salvando...');
    // As 15 respostas como estão nos campos, vazias inclusive (`5139-5142`).
    const registro = Object.fromEntries(CAMPOS.map((campo) => [campo, respostas[campo] ?? '']));
    const resultado = await salvarAvaliativo(dados, escopo, registro);
    // Este ponto do legado mostra a mensagem CRUA depois do prefixo, e não a tradução.
    if (!resultado.ok) {
      statusAvaliativo.escrever('Erro ao salvar: ' + resultado.erro.mensagem);
      return;
    }
    statusAvaliativo.mostrar('Salvo!');
  };

  return (
    <section className="flex flex-col gap-cx-3" data-testid="aba-avaliativo">
      <h3 className="text-cx-base font-semibold text-texto">Questionário Avaliativo</h3>
      <p className="text-cx-sm text-texto-suave">
        Para entender melhor sua relação com as cores e sua rotina de beleza, responda as perguntas abaixo com calma.
        Não existe resposta certa ou errada. Suas respostas vão me ajudar a personalizar as recomendações da sua
        consultoria.
      </p>
      {CAMPOS.map((campo) => (
        <CampoTexto
          key={campo}
          rotulo={PERGUNTAS_AVALIATIVO[campo] ?? campo}
          // Duas linhas, como o `rows="2"` do legado (`5130`).
          linhas={2}
          value={respostas[campo] ?? ''}
          // Consulta: campo desabilitado (`admin.html:3394`).
          disabled={somenteLeitura}
          onChange={(evento) => {
            // O valor lido antes do `setState`, porque o evento não vive até a função de atualização.
            const valor = evento.target.value;
            setRespostas((r) => ({ ...r, [campo]: valor }));
          }}
        />
      ))}
      {/* Consulta não vê botão de ação (`admin.html:3395`). O botão NÃO trava durante o envio, como
          no legado (`5136-5147`, anotado na linha 210 do inventário). */}
      {!somenteLeitura && (
        <div>
          <Botao disabled={!carregado} onClick={() => void salvar()}>
            Salvar / reenviar questionário avaliativo
          </Botao>
        </div>
      )}
      <LinhaDeStatus texto={status} testid="status-avaliativo" />
    </section>
  );
}
